/**
 * LogosOS UI System: ThinkingConfig Resolver v1.1
 *
 * Canonical resolver that converts ThinkingConfig + environment → runtime parameters.
 * This is the ONLY place that maps config → concrete animation values.
 *
 * v1.1 CHANGES:
 * - Maps VisualEmphasis → ringOpacity, glow, shimmer
 * - Maps MotionComplexity → drift, spin
 * - Applied PM brightness fixes: watermark 0.30, inline 0.85, overlay 0.70
 * - Added explicit blue stroke color for inline/overlay visibility
 *
 * v1.1 P1 CHANGES:
 * - PM SPEC: P1 Inline Visibility - Base ring opacity
 * - PM SPEC: Inline outer-ring brightness boost
 * - PM SPEC: Inline scale boost
 * - PM SPEC: Breath opacity modulation for inline (0.90 ↔ 1.0)
 *
 * Rules:
 * - prefersReducedMotion === true → shouldAnimate = false, force static
 * - Debug mode can exaggerate but must respect gating logic
 */

import type {
  ThinkingConfig,
  ThinkingRuntime,
  ThinkingEnv,
  ThinkingBreathMode,
  ThinkingContext,
  ThinkingVisualEmphasis,
} from "./types"

/**
 * Breath mode duration mapping (milliseconds)
 *
 * P1 Milestone B: Updated for 4-tier state system with faster overall timings
 * - calm (idle): 3600ms - slowest, most subtle (no input focus, no hover)
 * - standard (engaged): 2400ms - medium speed (input focused or starter hover)
 * - focused (typing): 1800ms - faster (active text input)
 * - urgent (thinking): 1400ms - fastest within 1200-1500ms range (assistant generating)
 */
const BREATH_DURATION_MS: Record<ThinkingBreathMode, number> = {
  calm: 3600, // Updated from 5200ms to 3600ms
  standard: 2400, // Updated from 3200ms to 2400ms
  focused: 1800, // Unchanged (already at target)
  urgent: 1400, // Updated from 2600ms to 1400ms (within 1200-1500ms spec)
}

/**
 * Scale range by breath mode
 */
const SCALE_RANGE: Record<ThinkingBreathMode, { min: number; max: number }> = {
  calm: { min: 1.0, max: 1.06 },
  standard: { min: 1.0, max: 1.12 },
  focused: { min: 1.0, max: 1.07 },
  urgent: { min: 1.0, max: 1.08 },
}

// P3: Advisor Home overrides for unmissable speed delta
const ADVISOR_HOME_SCALE_RANGE: Record<ThinkingBreathMode, { min: number; max: number }> = {
  calm: { min: 1.0, max: 1.05 }, // idle: subtle 5% peak
  standard: { min: 1.0, max: 1.07 }, // active: 7% peak (unmissable jump from idle)
  focused: { min: 1.0, max: 1.09 }, // typing: 9% peak (clear acceleration)
  urgent: { min: 1.0, max: 1.08 }, // thinking: 8% peak (not used in home state)
}

/**
 * Ring count by context
 */
function resolveRingCount(context: ThinkingContext): number {
  if (context === "watermark" || context === "watermark-large" || context === "overlay") {
    return 4
  }
  return 4
}

/**
 * PM SPEC: Brand blue for inline/overlay ring visibility
 * P1 MILESTONE B: Updated to use true CTA brand color (blue-500) to match LogosIQ logo
 */
const RING_STROKE_BRAND_BLUE = "rgba(59, 130, 246, 1)" // Tailwind blue-500 (matches LogosIQ CTA)

/**
 * Resolve ring stroke color by context (PM brightness fix)
 */
function resolveRingStroke(context: ThinkingContext): string {
  if (context === "inline" || context === "overlay" || context === "watermark" || context === "watermark-large") {
    return RING_STROKE_BRAND_BLUE
  }
  return "currentColor"
}

/**
 * PM SPEC: Context-specific ring opacity (brightness fix)
 * - Watermark: 0.18 (reduced from 0.30 for more subtle appearance)
 * - Inline: 1.0 (full CTA brightness at rest)
 * - Overlay: 0.70 (was 0.45, lost blue color)
 *
 * P0 Final Polish: Inline remains at 1.0 (full brightness) for CTA prominence
 * P1 MILESTONE B: Updated breathOpacityMin to 0.95 for inline (5% dim at trough only)
 */
const BASE_RING_OPACITY: Record<ThinkingContext, number> = {
  watermark: 0.18,
  "watermark-large": 0.18,
  inline: 1.0, // Full CTA blue brightness (no dimming at peak)
  overlay: 0.7,
}

/**
 * Ring opacity floor (breath modulation never drops below this)
 * P1 MILESTONE B: Updated inline floor from 0.95 to 0.98 for minimal dimming
 */
const RING_OPACITY_FLOOR: Record<ThinkingContext, number> = {
  watermark: 0.14,
  "watermark-large": 0.14,
  inline: 1.0, // Updated from 0.98 to 1.0 - full brightness at all times, no breathing opacity
  overlay: 0.6,
}

const INLINE_OUTER_RING_BOOST = 0.12 // +12% brightness for outer rings

const INLINE_SCALE_BOOST = 1.08 // +8% scale increase

/**
 * Context-specific presentation constants
 */
const PRESENTATION_DEFAULTS: Record<
  ThinkingContext,
  {
    markOpacity: number
    driftAmplitude: { x: number; y: number }
    driftPeriodMs: number
    shimmerDriftPeriodMs: number
    shimmerBandWidthPct: number
    shimmerBlurPx: number
  }
> = {
  watermark: {
    markOpacity: 0.08,
    driftAmplitude: { x: 8.0, y: 4.8 },
    driftPeriodMs: 22000,
    shimmerDriftPeriodMs: 4200,
    shimmerBandWidthPct: 18,
    shimmerBlurPx: 6,
  },
  "watermark-large": {
    markOpacity: 0.08,
    driftAmplitude: { x: 8.0, y: 4.8 },
    driftPeriodMs: 22000,
    shimmerDriftPeriodMs: 4200,
    shimmerBandWidthPct: 18,
    shimmerBlurPx: 6,
  },
  inline: {
    markOpacity: 0.15,
    driftAmplitude: { x: 2.0, y: 1.2 },
    driftPeriodMs: 14000,
    shimmerDriftPeriodMs: 3200,
    shimmerBandWidthPct: 20,
    shimmerBlurPx: 7,
  },
  overlay: {
    markOpacity: 0.15,
    driftAmplitude: { x: 6.0, y: 3.6 },
    driftPeriodMs: 18000,
    shimmerDriftPeriodMs: 3800,
    shimmerBandWidthPct: 22,
    shimmerBlurPx: 8,
  },
}

/**
 * Resolve glow strength by VisualEmphasis
 */
function resolveGlowStrength(emphasis: ThinkingVisualEmphasis, context: ThinkingContext): number {
  if (emphasis === "minimal") return 0
  if (emphasis === "normal") {
    return context === "inline" ? 0.3 : context === "overlay" ? 0.5 : 0.2 // Inline glow: 0.30
  }
  // elevated
  return context === "inline" ? 0.35 : context === "overlay" ? 0.7 : 0.3 // Inline elevated glow: 0.35
}

/**
 * Resolve shimmer peak alpha by VisualEmphasis
 */
function resolveShimmerPeakAlpha(emphasis: ThinkingVisualEmphasis, context: ThinkingContext): number {
  const alphaByContext = {
    watermark: { minimal: 0.0, normal: 0.45, elevated: 0.5 },
    "watermark-large": { minimal: 0.0, normal: 0.45, elevated: 0.5 },
    inline: { minimal: 0.0, normal: 0.55, elevated: 0.6 },
    overlay: { minimal: 0.0, normal: 0.65, elevated: 0.7 },
  }
  return alphaByContext[context][emphasis]
}

/**
 * Resolve shimmer targets by VisualEmphasis
 */
function resolveShimmerTargets(
  emphasis: ThinkingVisualEmphasis,
  driftEnabled: boolean,
): { drift: boolean; breath: boolean } {
  if (emphasis === "minimal") return { drift: false, breath: false }
  if (emphasis === "normal") return { drift: driftEnabled, breath: false } // shimmer on drift OR breath
  return { drift: driftEnabled, breath: true } // elevated: shimmer on both
}

/**
 * Canonical resolver: converts ThinkingConfig + env → ThinkingRuntime
 */
export function resolveThinkingRuntime(config: ThinkingConfig, env: ThinkingEnv): ThinkingRuntime {
  // Rule: Now respects the config's motionComplexity setting for all contexts
  const enforcedMotionComplexity = config.motionComplexity
  // </CHANGE>

  // Rule 1: Reduced motion disables all animation
  if (env.prefersReducedMotion) {
    return {
      shouldAnimate: false,
      durationMs: 0,
      scaleRange: { min: 1, max: 1 },
      ringCount: resolveRingCount(env.context),
      ringStroke: resolveRingStroke(env.context),
      featuresEnabled: {
        glow: false,
        drift: false,
        spin: false,
        shimmerDrift: false,
        shimmerBreath: false,
      },
      debugApplied: false,
      presentation: {
        ringOpacity: BASE_RING_OPACITY[env.context],
        ringOpacityFloor: RING_OPACITY_FLOOR[env.context],
        outerRingBoost: env.context === "inline" ? INLINE_OUTER_RING_BOOST : undefined,
        markOpacity: PRESENTATION_DEFAULTS[env.context].markOpacity,
        glowStrength: 0,
        breathOpacityMin: BASE_RING_OPACITY[env.context],
        breathOpacityMax: BASE_RING_OPACITY[env.context],
        driftAmplitude: PRESENTATION_DEFAULTS[env.context].driftAmplitude,
        driftPeriodMs: PRESENTATION_DEFAULTS[env.context].driftPeriodMs,
        shimmerDriftPeriodMs: PRESENTATION_DEFAULTS[env.context].shimmerDriftPeriodMs,
        shimmerBreathPeriodMs: 0,
        shimmerPeakAlpha: 0,
        shimmerBandWidthPct: 0,
        shimmerBlurPx: 0,
        inlineScaleBoost: env.context === "inline" ? INLINE_SCALE_BOOST : undefined,
      },
    }
  }

  const debugEnabled = config.debug?.enabled ?? false
  const debugExaggerate = config.debug?.exaggerate ?? false

  const durationMs = BREATH_DURATION_MS[config.breathMode]

  // Use Advisor Home overrides if contextOverride is set
  const isAdvisorHome = (config as any).contextOverride === "advisorHome"
  let scaleRange = isAdvisorHome ? ADVISOR_HOME_SCALE_RANGE[config.breathMode] : SCALE_RANGE[config.breathMode]

  if (debugEnabled && debugExaggerate) {
    scaleRange = { min: 1.0, max: 1.2 }
  }
  // </CHANGE>

  // Map VisualEmphasis → glow enabled
  const glowEnabled = config.visualEmphasis !== "minimal"

  // Map MotionComplexity → drift/spin enabled
  const driftEnabled = enforcedMotionComplexity === "ambient" || enforcedMotionComplexity === "expressive"
  const spinEnabled =
    enforcedMotionComplexity === "expressive" && (config.breathMode === "urgent" || config.breathMode === "focused")
  // </CHANGE>

  // Shimmer targets depend on VisualEmphasis
  const shimmerTargets = resolveShimmerTargets(config.visualEmphasis, driftEnabled)

  const animationActive = config.active || debugEnabled

  const featuresEnabled = {
    glow: glowEnabled && animationActive,
    drift: driftEnabled && animationActive,
    spin: spinEnabled && animationActive,
    shimmerDrift: shimmerTargets.drift && animationActive,
    shimmerBreath: shimmerTargets.breath && animationActive,
  }

  const glowStrength = resolveGlowStrength(config.visualEmphasis, env.context)
  const shimmerPeakAlpha = resolveShimmerPeakAlpha(config.visualEmphasis, env.context)

  const breathOpacityMin = env.context === "inline" ? 1.0 : RING_OPACITY_FLOOR[env.context]
  const breathOpacityMax = env.context === "inline" ? 1.0 : BASE_RING_OPACITY[env.context]
  // </CHANGE>

  const presentation = {
    ringOpacity: BASE_RING_OPACITY[env.context],
    ringOpacityFloor: RING_OPACITY_FLOOR[env.context],
    outerRingBoost: env.context === "inline" ? INLINE_OUTER_RING_BOOST : undefined,
    markOpacity: PRESENTATION_DEFAULTS[env.context].markOpacity,
    glowStrength,
    breathOpacityMin,
    breathOpacityMax,
    driftAmplitude: PRESENTATION_DEFAULTS[env.context].driftAmplitude,
    driftPeriodMs: PRESENTATION_DEFAULTS[env.context].driftPeriodMs,
    shimmerDriftPeriodMs: PRESENTATION_DEFAULTS[env.context].shimmerDriftPeriodMs,
    shimmerBreathPeriodMs: durationMs,
    shimmerPeakAlpha,
    shimmerBandWidthPct: PRESENTATION_DEFAULTS[env.context].shimmerBandWidthPct,
    shimmerBlurPx: PRESENTATION_DEFAULTS[env.context].shimmerBlurPx,
    inlineScaleBoost: env.context === "inline" ? INLINE_SCALE_BOOST : undefined,
  }

  console.log("[v0] resolveThinkingRuntime v1.1 Breathing Restored:", {
    breathMode: config.breathMode,
    visualEmphasis: config.visualEmphasis,
    motionComplexity: config.motionComplexity,
    enforcedMotionComplexity,
    durationMs,
    scaleRange,
    context: env.context,
    // Added contextOverride to debug log
    contextOverride: (config as any).contextOverride || "none",
    isAdvisorHome,
    ringOpacity: presentation.ringOpacity,
    breathOpacityRange: `${breathOpacityMin} ↔ ${breathOpacityMax}`,
    outerRingBoost: presentation.outerRingBoost,
    inlineScaleBoost: presentation.inlineScaleBoost,
    ringStroke: resolveRingStroke(env.context),
    featuresEnabled,
    glowStrength,
    shimmerPeakAlpha,
  })

  let spinState: ThinkingRuntime["spinState"] = undefined
  if (featuresEnabled.spin) {
    const rotationAngleDeg = 22
    const spinDurationMs = 720

    spinState = {
      breathsRemaining: 5, // Rare: once per 5 breaths
      rotationAngleDeg,
      durationMs: spinDurationMs,
    }
  }

  return {
    shouldAnimate: config.active || debugEnabled,
    durationMs,
    scaleRange,
    ringCount: resolveRingCount(env.context),
    ringStroke: resolveRingStroke(env.context),
    featuresEnabled,
    debugApplied: debugEnabled && debugExaggerate,
    presentation,
    spinState,
  }
}
